import { useOutletContext } from "react-router-dom";
import { useComfort } from "../ComfortContext";
import {
  getDictionary,
  LOCALES,
  LOCALE_LABEL,
  type Locale,
} from "../../i18n/dictionary";
import { accounts } from "../../domain/fixtures";
import { FIXTURE_ACCOUNT_ID } from "./data";
import { shouldUseSupabase } from "../../data";

/** Context the ServicePortal shell passes down to caregiver screens. */
export interface ServiceOutletContext {
  signOut: () => void;
  /** Signed-in Supabase user id (real-auth path); undefined in the demo path. */
  userId?: string;
  /** Signed-in account email (real-auth path); undefined in the demo path. */
  email?: string;
}

type ComfortKey = "calm" | "bigText" | "spacing" | "contrast" | "dark";

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        boxShadow: "var(--sh-1)",
        padding: "20px 22px",
        marginTop: 18,
      }}
    >
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: ".1em",
          textTransform: "uppercase",
          color: "var(--ink-faint)",
          margin: "0 0 14px",
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

export function ServiceSettings() {
  const { calm, bigText, spacing, contrast, dark, locale, toggle, setLocale } =
    useComfort();
  const ctx = useOutletContext<ServiceOutletContext | null>();
  const dict = getDictionary(locale);
  // The signed-in email comes from the shell's Outlet context (ServicePortal
  // passes { signOut, userId, email }). Falling back to the FIXTURE account here
  // meant the live site told every signed-in caregiver they were
  // "lena@brightsteps.org" — a stranger's address, on the one screen whose job
  // is to tell you which account you are using. The fixture value is kept ONLY
  // for the offline/demo path, where there is no session to read.
  const email =
    ctx?.email ??
    (shouldUseSupabase()
      ? "Signed in"
      : (accounts.find((a) => a.id === FIXTURE_ACCOUNT_ID)?.email ??
        "you@example.com"));

  const comfortRows: { key: ComfortKey; label: string; on: boolean }[] = [
    { key: "calm", label: dict.comfort.calmMode, on: calm },
    { key: "bigText", label: dict.comfort.biggerText, on: bigText },
    { key: "spacing", label: dict.comfort.readingSpacing, on: spacing },
    { key: "contrast", label: dict.comfort.higherContrast, on: contrast },
    { key: "dark", label: dict.comfort.darkTheme, on: dark },
  ];

  return (
    <div>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 28,
          color: "var(--ink-deep)",
          margin: "0 0 8px",
        }}
      >
        {dict.service.settings.title}
      </h1>
      <p style={{ color: "var(--ink-soft)", fontSize: 15.5, margin: 0, lineHeight: 1.6 }}>
        {dict.service.settings.subtitle}
      </p>

      {/* Language */}
      <SectionCard title={dict.language.title}>
        <p style={{ color: "var(--ink-soft)", fontSize: 14.5, margin: "0 0 14px", lineHeight: 1.55 }}>
          {dict.language.subtitle}
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {LOCALES.map((loc: Locale) => {
            const active = loc === locale;
            return (
              <button
                key={loc}
                onClick={() => setLocale(loc)}
                aria-pressed={active}
                style={{
                  minHeight: 48,
                  padding: "0 20px",
                  borderRadius: "var(--r-pill)",
                  border: `1.5px solid ${active ? "var(--brand)" : "var(--line)"}`,
                  background: active ? "var(--brand-tint)" : "transparent",
                  color: active ? "var(--brand-hover)" : "var(--ink)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: "pointer",
                }}
              >
                {LOCALE_LABEL[loc]}
              </button>
            );
          })}
        </div>
      </SectionCard>

      {/* Comfort & display */}
      <SectionCard title={dict.comfort.title}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {comfortRows.map((row, idx) => (
            <div
              key={row.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                minHeight: 56,
                borderTop: idx === 0 ? "none" : "1px solid var(--line)",
              }}
            >
              <span style={{ fontSize: 15, color: "var(--ink)" }}>{row.label}</span>
              <button
                role="switch"
                aria-checked={row.on}
                aria-label={row.label}
                onClick={() => toggle(row.key)}
                style={{
                  flex: "none",
                  minHeight: 48,
                  minWidth: 96,
                  padding: "0 18px",
                  borderRadius: "var(--r-pill)",
                  border: `1.5px solid ${row.on ? "var(--brand)" : "var(--line)"}`,
                  background: row.on ? "var(--brand-tint)" : "transparent",
                  color: row.on ? "var(--brand-hover)" : "var(--ink-soft)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                {row.on ? "On" : "Off"}
              </button>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Account */}
      <SectionCard title={dict.service.settings.accountTitle}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <span style={{ fontSize: 12.5, color: "var(--ink-faint)" }}>
              {dict.service.settings.signedInAs}
            </span>
            <span style={{ display: "block", fontSize: 15, color: "var(--ink-deep)", fontWeight: 600 }}>
              {email}
            </span>
            <span style={{ fontSize: 13.5, color: "var(--ink-faint)" }}>
              {dict.service.settings.signInMethod}: {dict.service.settings.magicLink}
            </span>
          </div>
          <button
            onClick={() => ctx?.signOut()}
            style={{
              minHeight: 48,
              padding: "0 22px",
              borderRadius: "var(--r-pill)",
              border: "1.5px solid var(--line-strong)",
              background: "transparent",
              color: "var(--ink)",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            {dict.common.signOut}
          </button>
        </div>
      </SectionCard>
    </div>
  );
}
